import { create } from 'zustand';
import { db } from '@/offline/db';
import { enqueueSync } from '@/offline/sync';
import { supabase } from '@/lib/supabase';
import {
  RI_PROTOCOLS,
  resolveProtocol,
  emptyProtocolState,
  activeStepsForProtocol,
  permittedMedications,
  permittedProcedures,
  type Protocol,
  type ProtocolExecutionState,
  type ProtocolDecisionRecord,
  type ProtocolStepCompletion,
  type ProtocolDeviation,
} from '@moquintrend/protocol-engine';
import type { EPCR } from '@/types';

/**
 * ProtocolExecutionStore — owns the runtime state for which protocols
 * are active on the current ePCR, which decisions have been made, and
 * which steps are done.
 *
 * The store is ePCR-scoped: loadForEpcr() swaps the state when navigating
 * between ePCRs. All mutations persist to IndexedDB immediately and queue
 * for server sync.
 */

interface ProtocolStoreState {
  epcrId: string | null;
  state: ProtocolExecutionState;
  protocols: Protocol[];

  /** Load state for an ePCR. Creates default state if none exists. */
  loadForEpcr: (epcr: EPCR) => Promise<void>;
  /** Re-run trigger matching (e.g., after chief_complaint/impression change). */
  evaluateTriggers: (ctx: {
    dispatchReason?: string | null;
    chiefComplaint?: string | null;
    primaryImpression?: string | null;
  }) => Promise<void>;

  /** Explicitly activate a protocol (moves from triggered/related → active). */
  activateProtocol: (protocolId: string) => Promise<void>;
  /** Deactivate a protocol (hides its steps from active list, keeps history). */
  deactivateProtocol: (protocolId: string) => Promise<void>;
  /** Set the primary protocol. */
  setPrimaryProtocol: (protocolId: string) => Promise<void>;

  /** Record a decision at a protocol step. Overwrites prior decision at same step. */
  recordDecision: (args: {
    protocolId: string;
    stepOrder: number;
    choice: string;
    rationale?: string;
  }) => Promise<void>;

  /** Mark a protocol step as completed. Optionally link to a procedure/medication id. */
  completeStep: (args: {
    protocolId: string;
    stepOrder: number;
    linkedProcedureId?: string;
    linkedMedicationId?: string;
    note?: string;
  }) => Promise<void>;

  /** Remove a step completion (e.g., user unchecked the box). */
  uncompleteStep: (protocolId: string, stepOrder: number) => Promise<void>;

  /** Document a deviation from protocol. */
  addDeviation: (args: {
    protocolId: string;
    stepOrder: number | null;
    reason: string;
    actionType?: 'medication' | 'procedure' | 'omission';
    actionId?: string;
  }) => Promise<void>;

  /** Convenience: get all protocols that are "active" in the UI. */
  activeProtocols: () => Protocol[];
  /** Convenience: get all triggered (but not necessarily active) protocols. */
  triggeredProtocols: () => Protocol[];
  /** Convenience: get the primary protocol object. */
  primaryProtocol: () => Protocol | null;
  /** Convenience: get related protocols. */
  relatedProtocols: () => Protocol[];
  /** Convenience: is this protocol/step completed? */
  isStepComplete: (protocolId: string, stepOrder: number) => boolean;
  /** Convenience: get decision choice for a step. */
  decisionFor: (protocolId: string, stepOrder: number) => string | null;
  /** Convenience: get active steps for a protocol (respects requires* conditions). */
  activeStepsFor: (protocol: Protocol) => ReturnType<typeof activeStepsForProtocol>;
  /** Convenience: list of allowed medications right now (for MedicationsTab). */
  permittedMedications: () => ReturnType<typeof permittedMedications>;
  /** Convenience: list of allowed procedures right now (for ProceduresTab). */
  permittedProcedures: () => ReturnType<typeof permittedProcedures>;
  /** Does any active protocol contain this medication? */
  isMedicationAllowed: (drugName: string) => { allowed: boolean; protocolId?: string; stepOrder?: number };
  /** Does any active protocol contain this procedure? */
  isProcedureAllowed: (procedureType: string) => { allowed: boolean; protocolId?: string; stepOrder?: number };
}

export const useProtocolStore = create<ProtocolStoreState>((set, get) => ({
  epcrId: null,
  state: emptyProtocolState(),
  protocols: RI_PROTOCOLS,

  async loadForEpcr(epcr) {
    const existing = (epcr.protocol_state ?? emptyProtocolState()) as ProtocolExecutionState;
    set({ epcrId: epcr.id, state: existing });

    // If nothing has been evaluated yet, do the initial match based on current ePCR fields
    if (!existing.last_evaluated_at && (epcr.dispatch_reason || epcr.chief_complaint || epcr.primary_impression)) {
      await get().evaluateTriggers({
        dispatchReason: epcr.dispatch_reason,
        chiefComplaint: epcr.chief_complaint,
        primaryImpression: epcr.primary_impression,
      });
    }
  },

  async evaluateTriggers(ctx) {
    const matches = resolveProtocol(ctx);
    const state = get().state;
    const triggered_protocol_ids = matches.map((p) => p.id);

    // Collect "related" protocols from the matched set's relatedProtocolIds
    const relatedSet = new Set<string>();
    for (const p of matches) {
      (p.relatedProtocolIds ?? []).forEach((id) => relatedSet.add(id));
      (p.alternativeProtocolIds ?? []).forEach((id) => relatedSet.add(id));
    }
    // Remove any that are already in triggered
    triggered_protocol_ids.forEach((id) => relatedSet.delete(id));

    const related_protocol_ids = Array.from(relatedSet);

    // Auto-select first triggered protocol as primary + active if nothing was active before
    let primary_protocol_id = state.primary_protocol_id;
    let active_protocol_ids = state.active_protocol_ids;
    if (!primary_protocol_id && triggered_protocol_ids[0]) {
      primary_protocol_id = triggered_protocol_ids[0];
    }
    if (active_protocol_ids.length === 0 && primary_protocol_id) {
      active_protocol_ids = [primary_protocol_id];
    }

    // Stamp the version + effective date for any protocol that's now
    // active (and not already stamped). Stamps are write-once: once a
    // protocol has been activated on this ePCR, the version that drove
    // the call is FROZEN. Subsequent edits, hot-fixes, or annual
    // updates do NOT overwrite the original stamp.
    const protocol_versions = { ...state.protocol_versions };
    const protocol_effective_dates = { ...state.protocol_effective_dates };
    for (const id of active_protocol_ids) {
      if (protocol_versions[id]) continue;
      const proto = RI_PROTOCOLS.find((p) => p.id === id);
      if (proto) {
        protocol_versions[id] = proto.version;
        protocol_effective_dates[id] = proto.effectiveDate;
      }
    }

    const next: ProtocolExecutionState = {
      ...state,
      triggered_protocol_ids,
      primary_protocol_id,
      related_protocol_ids,
      active_protocol_ids,
      protocol_versions,
      protocol_effective_dates,
      last_evaluated_at: new Date().toISOString(),
    };

    await persist(get().epcrId, next, set, 'trigger_protocol');
  },

  async activateProtocol(protocolId) {
    const state = get().state;
    if (state.active_protocol_ids.includes(protocolId)) return;
    // Stamp version + effective date at the moment of activation, but
    // ONLY if not already stamped. This is the legal chain-of-custody
    // anchor — once captured it never changes for this ePCR.
    const protocol_versions = { ...state.protocol_versions };
    const protocol_effective_dates = { ...state.protocol_effective_dates };
    if (!protocol_versions[protocolId]) {
      const proto = RI_PROTOCOLS.find((p) => p.id === protocolId);
      if (proto) {
        protocol_versions[protocolId] = proto.version;
        protocol_effective_dates[protocolId] = proto.effectiveDate;
      }
    }
    const next: ProtocolExecutionState = {
      ...state,
      active_protocol_ids: [...state.active_protocol_ids, protocolId],
      protocol_versions,
      protocol_effective_dates,
    };
    await persist(get().epcrId, next, set, 'activate_protocol', { protocol_id: protocolId });
  },

  async deactivateProtocol(protocolId) {
    const state = get().state;
    const next: ProtocolExecutionState = {
      ...state,
      active_protocol_ids: state.active_protocol_ids.filter((id) => id !== protocolId),
      primary_protocol_id: state.primary_protocol_id === protocolId ? null : state.primary_protocol_id,
    };
    await persist(get().epcrId, next, set, 'deactivate_protocol', { protocol_id: protocolId });
  },

  async setPrimaryProtocol(protocolId) {
    const state = get().state;
    const active = state.active_protocol_ids.includes(protocolId)
      ? state.active_protocol_ids
      : [...state.active_protocol_ids, protocolId];
    const next: ProtocolExecutionState = {
      ...state,
      primary_protocol_id: protocolId,
      active_protocol_ids: active,
    };
    await persist(get().epcrId, next, set, 'set_primary', { protocol_id: protocolId });
  },

  async recordDecision({ protocolId, stepOrder, choice, rationale }) {
    const state = get().state;
    const userId = await currentUserId();
    const filtered = state.decisions.filter(
      (d) => !(d.protocol_id === protocolId && d.step_order === stepOrder),
    );
    const rec: ProtocolDecisionRecord = {
      protocol_id: protocolId,
      step_order: stepOrder,
      choice,
      decided_at: new Date().toISOString(),
      decided_by: userId,
      rationale,
    };
    const next: ProtocolExecutionState = { ...state, decisions: [...filtered, rec] };
    await persist(get().epcrId, next, set, 'decide', {
      protocol_id: protocolId, step_order: stepOrder, choice,
    });
  },

  async completeStep({ protocolId, stepOrder, linkedProcedureId, linkedMedicationId, note }) {
    const state = get().state;
    const userId = await currentUserId();
    // If already complete, update (idempotent)
    const filtered = state.completed_steps.filter(
      (c) => !(c.protocol_id === protocolId && c.step_order === stepOrder),
    );
    const rec: ProtocolStepCompletion = {
      protocol_id: protocolId,
      step_order: stepOrder,
      completed_at: new Date().toISOString(),
      completed_by: userId,
      linked_procedure_id: linkedProcedureId,
      linked_medication_id: linkedMedicationId,
      note,
    };
    const next: ProtocolExecutionState = { ...state, completed_steps: [...filtered, rec] };
    await persist(get().epcrId, next, set, 'complete_step', {
      protocol_id: protocolId, step_order: stepOrder,
      linked_procedure_id: linkedProcedureId,
      linked_medication_id: linkedMedicationId,
    });
  },

  async uncompleteStep(protocolId, stepOrder) {
    const state = get().state;
    const next: ProtocolExecutionState = {
      ...state,
      completed_steps: state.completed_steps.filter(
        (c) => !(c.protocol_id === protocolId && c.step_order === stepOrder),
      ),
    };
    await persist(get().epcrId, next, set, 'reopen_step', { protocol_id: protocolId, step_order: stepOrder });
  },

  async addDeviation({ protocolId, stepOrder, reason, actionType, actionId }) {
    const state = get().state;
    const userId = await currentUserId();
    const rec: ProtocolDeviation = {
      protocol_id: protocolId,
      step_order: stepOrder,
      reason,
      documented_at: new Date().toISOString(),
      documented_by: userId,
      action_type: actionType,
      action_id: actionId,
    };
    const next: ProtocolExecutionState = { ...state, deviations: [...state.deviations, rec] };
    await persist(get().epcrId, next, set, 'deviate', {
      protocol_id: protocolId, step_order: stepOrder, reason, action_type: actionType, action_id: actionId,
    });
  },

  activeProtocols() {
    const { state, protocols } = get();
    return state.active_protocol_ids
      .map((id) => protocols.find((p) => p.id === id))
      .filter((p): p is Protocol => !!p);
  },
  triggeredProtocols() {
    const { state, protocols } = get();
    return state.triggered_protocol_ids
      .map((id) => protocols.find((p) => p.id === id))
      .filter((p): p is Protocol => !!p);
  },
  primaryProtocol() {
    const { state, protocols } = get();
    return state.primary_protocol_id
      ? protocols.find((p) => p.id === state.primary_protocol_id) ?? null
      : null;
  },
  relatedProtocols() {
    const { state, protocols } = get();
    return state.related_protocol_ids
      .map((id) => protocols.find((p) => p.id === id))
      .filter((p): p is Protocol => !!p);
  },
  isStepComplete(protocolId, stepOrder) {
    return get().state.completed_steps.some(
      (c) => c.protocol_id === protocolId && c.step_order === stepOrder,
    );
  },
  decisionFor(protocolId, stepOrder) {
    return (
      get().state.decisions.find(
        (d) => d.protocol_id === protocolId && d.step_order === stepOrder,
      )?.choice ?? null
    );
  },
  activeStepsFor(protocol) {
    return activeStepsForProtocol(protocol, get().state);
  },
  permittedMedications() {
    return permittedMedications(get().protocols, get().state);
  },
  permittedProcedures() {
    return permittedProcedures(get().protocols, get().state);
  },
  isMedicationAllowed(drugName) {
    const allowed = get().permittedMedications();
    const hit = allowed.find((m) => m.drugName.toLowerCase() === drugName.toLowerCase());
    return hit
      ? { allowed: true, protocolId: hit.protocolId, stepOrder: hit.stepOrder }
      : { allowed: false };
  },
  isProcedureAllowed(procedureType) {
    const allowed = get().permittedProcedures();
    const hit = allowed.find((p) => p.procedureType === procedureType);
    return hit
      ? { allowed: true, protocolId: hit.protocolId, stepOrder: hit.stepOrder }
      : { allowed: false };
  },
}));

// ============================================================
// Helpers
// ============================================================

async function currentUserId(): Promise<string> {
  try {
    const { data } = await supabase.auth.getUser();
    return data.user?.id ?? 'dev-user-001';
  } catch {
    return 'dev-user-001';
  }
}

async function persist(
  epcrId: string | null,
  next: ProtocolExecutionState,
  set: (partial: Partial<ProtocolStoreState>) => void,
  auditAction: string,
  auditDetail?: Record<string, unknown>,
) {
  // Optimistic UI update
  set({ state: next });

  if (!epcrId) return;

  // Persist to IndexedDB
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    await db.epcrs.update(epcrId, { protocol_state: next, _dirty: true, _last_modified: new Date().toISOString() } as any);
  } catch {
    // ignore — offline ok, will sync when online
  }

  // Queue server sync for ePCR (carries the whole protocol_state update)
  await enqueueSync('epcrs', epcrId, 'update', { id: epcrId, protocol_state: next });

  // Append audit log (best-effort, doesn't block UI)
  try {
    const userId = await currentUserId();
    const epcr = await db.epcrs.get(epcrId);
    if (epcr) {
      await enqueueSync('protocol_audit_log', crypto.randomUUID(), 'insert', {
        epcr_id: epcrId,
        agency_id: epcr.agency_id,
        user_id: userId,
        action: auditAction,
        detail: auditDetail ?? {},
      });
    }
  } catch {
    /* non-fatal */
  }
}
